/**
 * People as the dashboard's pages know them: an email, lower case, and a
 * name when there is one. Shared by the Teams page and the Roadmap, and by
 * the person picker (`@/components/person-picker`) both use.
 */

import { matchesSearch } from "@/lib/search";

/** Anyone a page can name or pick: an email and, when known, a first and last name. */
export interface NamedPerson {
  email: string;
  name?: string | null;
}

/** An email as the server stores it: trimmed, lower case. */
export function normalEmail(text: string): string {
  return text.trim().toLowerCase();
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const EMAIL_MAX_LENGTH = 254;
/** Microsoft's mark of a guest account, which the connector never admits. */
const GUEST_MARKER = "#ext#";

/** Why a typed address cannot be added: no full address, or one outside the allowed domains. */
export type EmailProblem = "invalid" | "domain";

/**
 * Whether a typed address can be added, as the server checks it: one full
 * address, no guest's, in one of the allowed domains (any while none is known).
 */
export function emailProblem(text: string, domains: readonly string[]): EmailProblem | null {
  const email = normalEmail(text);
  if (email.length > EMAIL_MAX_LENGTH || !EMAIL.test(email) || email.includes(GUEST_MARKER)) {
    return "invalid";
  }
  const domain = email.slice(email.lastIndexOf("@") + 1);
  if (domains.length > 0 && !domains.some((allowed) => allowed.toLowerCase() === domain)) {
    return "domain";
  }
  return null;
}

/** How a person is shown: their name, else their email. */
export function personLabel(person: NamedPerson): string {
  return person.name?.trim() || person.email;
}

/** People by their email, lower case. */
export function byEmail<T extends NamedPerson>(people: readonly T[]): Map<string, T> {
  return new Map(people.map((person) => [normalEmail(person.email), person]));
}

/** Whether a person matches a search: every word of it in their name or email, in any order. */
export function matchesPerson(person: NamedPerson, query: string): boolean {
  return matchesSearch([person.name, person.email], query);
}

/** The first word of a person's name; empty without one. */
function firstWord(person: NamedPerson): string {
  return person.name?.trim().split(/\s+/)[0] ?? "";
}

/**
 * How a person is named in a short line, such as "Wacht op An en Bram": the
 * first word of their name, else their email's part before the @.
 */
function shortName(person: NamedPerson): string {
  return firstWord(person) || person.email.split("@")[0] || person.email;
}

/**
 * How the Roadmap names people, De Ceuster being small enough that people go
 * by their first name: the first word of their name, else their email's part
 * before the @. When two or more of the people the page knows (`known`, the
 * dashboard's people) share a first name, whatever its case, each of them
 * keeps their full name. DC Data's roadmap tools name people by the same rule.
 */
export function firstNameLabel(known: Iterable<NamedPerson>): (person: NamedPerson) => string {
  const emails = new Set<string>();
  const counts = new Map<string, number>();
  for (const person of known) {
    const email = normalEmail(person.email);
    const first = firstWord(person).toLowerCase();
    if (!first || emails.has(email)) {
      continue;
    }
    emails.add(email);
    counts.set(first, (counts.get(first) ?? 0) + 1);
  }
  return (person) => {
    const shared = (counts.get(firstWord(person).toLowerCase()) ?? 0) > 1;
    return shared ? personLabel(person) : shortName(person);
  };
}

/**
 * A person's avatar letters, upper case: the first letters of their first
 * and last word ("AP" for An Peeters, "A" for An), else their email's first
 * letter; "?" for neither.
 */
export function personInitials(person: NamedPerson): string {
  const words = (person.name ?? "").trim().split(/\s+/).filter(Boolean);
  // Array.from splits by code point, so a name starting outside the BMP stays whole.
  const initial = (word: string) => Array.from(word)[0] ?? "";
  const letters =
    words.length > 1
      ? initial(words[0] ?? "") + initial(words.at(-1) ?? "")
      : initial(words[0] ?? person.email.trim());
  return (letters || "?").toUpperCase();
}
